import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Sun, TreeDeciduous, Shield, Award, Layers, Footprints, Flame, Zap, TreePine, Droplets } from "lucide-react";
import { SEO } from "../components/SEO";

export default function BambooPage() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-[#F5F5F0]"
    >
      <SEO
        path="/le-bambou"
        title="Le Bambou — Matériau Écologique et Ultra-Résistant"
        description="Le bambou densifié REBO : 3x plus dur que le chêne, résistant aux termites, classe 4, idéal pour le climat tropical de La Réunion. Découvrez pourquoi le bambou est l'alternative écologique parfaite au bois exotique."
      />
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="py-16 md:py-24 bg-[#2D2D2D] relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#E8E8E8] mb-6"
                style={{ fontFamily: "'DM Serif Display', serif" }}
                data-testid="bamboo-page-title"
              >
                Le bambou <span className="text-[#8BC34A]">REBO</span> :{" "}
                performance et <span className="text-[#8BC34A]">écologie</span>
              </h1>
              <p className="text-lg md:text-xl text-[#9E9E9E] max-w-3xl mx-auto">
                ProBwa distribue à La Réunion les produits REBO Bamboo, 
                référence mondiale du bambou tressé carbonisé. Un matériau 
                certifié, performant et respectueux de l'environnement.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Bloc 1: Le bambou - Forêt */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Leaf className="w-8 h-8 text-[#8BC34A]" />
                  <h2
                    className="text-3xl md:text-4xl font-bold text-[#2D2D2D]"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    LE BAMBOU
                  </h2>
                </div>
                <p className="text-lg text-[#3A3A3A] mb-6 leading-relaxed">
                  Le bambou tressé pousse jusqu'à un mètre par jour dans les forêts 
                  tropicales d'Asie. Après la récolte, la plante se régénère 
                  d'elle-même sans replantation, préservant ainsi les écosystèmes forestiers.
                </p>
                <p className="text-lg text-[#3A3A3A] leading-relaxed">
                  Une fois carbonisé entre 160 et 190 degrés Celsius, le bambou tressé 
                  atteint une densité de 1200 kg/m3, comparable à l'ipé ou au cumaru. 
                  Cette transformation lui confère une stabilité dimensionnelle remarquable.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border-2 border-[#8BC34A]/20">
                  <img
                    src="https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/yem4a7dk_Gemini_Generated_Image_7oaj4d7oaj4d7oaj.png"
                    alt="Forêt de bambou"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Bloc 2: Espaces extérieurs - Terrasse */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="order-2 lg:order-1"
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border-2 border-[#8BC34A]/20">
                  <img
                    src="https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/ufsvklcl_Lame%20TROPIC%20Intense%20r%C3%A9alisation.jpeg"
                    alt="Terrasse bambou avec piscine"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="order-1 lg:order-2"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Sun className="w-8 h-8 text-[#8BC34A]" />
                  <h2
                    className="text-3xl md:text-4xl font-bold text-[#2D2D2D]"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    POUR VOS ESPACES EXTÉRIEURS
                  </h2>
                </div>
                <p className="text-lg text-[#3A3A3A] mb-6 leading-relaxed">
                  En terrasse, le bambou tressé carbonisé offre un confort pieds nus 
                  et une résistance naturelle à l'humidité. Sa dureté exceptionnelle 
                  le protège des chocs, des rayures et du passage répété.
                </p>
                <p className="text-lg text-[#3A3A3A] leading-relaxed">
                  Nos lames se posent avec des clips Inox A4 et un aboutage rainure/languette 
                  pour une finition sans vis apparentes. Un entretien à l'huile une à deux fois 
                  par an suffit pour conserver son éclat naturel.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Bloc 3: Interieur - Parquet */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <TreeDeciduous className="w-8 h-8 text-[#8BC34A]" />
                  <h2
                    className="text-3xl md:text-4xl font-bold text-[#2D2D2D]"
                    style={{ fontFamily: "'DM Serif Display', serif" }}
                  >
                    POUR VOTRE INTÉRIEUR
                  </h2>
                </div>
                <p className="text-lg text-[#3A3A3A] mb-6 leading-relaxed">
                  Avec ses faibles variations dimensionnelles, le bambou tressé 
                  est idéal pour le parquet, l'habillage mural ou les faux plafonds. 
                  Sa finition pré-huilée et ses fibres apparentes apportent 
                  une élégance chaleureuse à chaque pièce.
                </p>
                <p className="text-lg text-[#3A3A3A] leading-relaxed">
                  Plus résistant que le PVC ou le composite, le bambou est aussi 
                  un choix sain : sans formaldéhyde (E0), il contribue à un intérieur 
                  naturel et agréable.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border-2 border-[#8BC34A]/20">
                  <img
                    src="https://customer-assets.emergentagent.com/job_44e06a86-26a6-4a1c-a1e6-5ebd3ec28cc3/artifacts/jngmfbs2_parquet%20pose.jpg"
                    alt="Parquet interieur bambou ProBwa"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Certifications REBO */}
        <section className="py-16 md:py-24 bg-[#2D2D2D]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2
                className="text-3xl md:text-4xl font-bold text-[#E8E8E8] mb-4"
                style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                Certifications <span className="text-[#8BC34A]">REBO</span>
              </h2>
              <p className="text-base text-[#9E9E9E] max-w-2xl mx-auto">
                Tous nos produits sont certifiés selon les normes européennes les plus exigeantes.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { title: "Haute dureté", desc: "Densité 1,2 g/cm3 - Brinell 107 N/mm2 (EN 1534)", Icon: Shield, color: "#8BC34A" },
                { title: "Durabilité Classe 1", desc: "Classe 1 (EN350) testée par RISE Bioéconomie", Icon: Award, color: "#FFD54F" },
                { title: "Classe d'utilisation 4", desc: "Usage extérieur en contact avec le sol (EN335)", Icon: Layers, color: "#81C784" },
                { title: "Antidérapage R10", desc: "Conforme DIN 51130 et TS 16165 par INTERTEK", Icon: Footprints, color: "#90A4AE" },
                { title: "Ignifuge", desc: "Conforme EN 13501-1 par EPH laboratoire", Icon: Flame, color: "#FF8A65" },
                { title: "Formaldéhyde E0", desc: "Émission conforme EN 13986 par SGS", Icon: Droplets, color: "#4FC3F7" },
                { title: "Résistance", desc: "87 N/mm2 courbage, 18700 N/mm2 élasticité (EN408)", Icon: Zap, color: "#FFF176" },
                { title: "Écologique", desc: "Croissance 5 ans, régénération naturelle, sans pesticides", Icon: TreePine, color: "#66BB6A" },
              ].map((cert, index) => (
                <motion.div
                  key={cert.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="p-5 md:p-6 rounded-2xl border border-[#ffffff08] relative overflow-hidden group hover:border-[#8BC34A]/30 transition-all duration-300"
                  style={{ background: "linear-gradient(145deg, #333 0%, #2A2A2A 100%)" }}
                  data-testid={`certification-${index}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${cert.color}15` }}
                  >
                    <cert.Icon className="w-6 h-6" style={{ color: cert.color }} />
                  </div>
                  <h4 className="text-[#E8E8E8] font-bold text-sm mb-2">{cert.title}</h4>
                  <p className="text-[#888] text-xs leading-relaxed">{cert.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-[#8BC34A]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <Link
              to="/nos-produits"
              className="inline-flex items-center gap-2 bg-[#2D2D2D] text-[#E8E8E8] rounded-full px-8 py-4 text-lg font-semibold hover:bg-[#3A3A3A] transition-all duration-300 shadow-lg"
              data-testid="view-products-cta"
            >
              VOIR NOS PRODUITS
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
}
